import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import StartIcon from '@mui/icons-material/Start';
import {
    Box,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';

type LessonPlanRole = 'payer' | 'athlete' | 'coach' | 'parent';

const DEFAULT_SCRIPTS = { open: '', on_neutral: '', on_negative: '', on_positive: '' };

interface ScriptsDisplayProps {
    scripts?: {
        open?: string;
        on_neutral?: string;
        on_negative?: string;
        on_positive?: string;
    } | null;
    myRoles?: LessonPlanRole[];
}

const ScriptsDisplay: React.FC<ScriptsDisplayProps> = ({ scripts: scriptsProp, myRoles }) => {
    const theme = useTheme();
    const scripts = scriptsProp && typeof scriptsProp === 'object' ? scriptsProp : DEFAULT_SCRIPTS;

    const scriptTypes = [
        { key: 'on_neutral' as const, label: 'Reframing on neutral response', icon: <SentimentNeutralIcon color="disabled" fontSize="small" />, color: 'default' as const },
        { key: 'on_negative' as const, label: 'Reframing on negative response', icon: <SentimentDissatisfiedIcon color="error" fontSize="small" />, color: 'error' as const },
        { key: 'on_positive' as const, label: 'Reframing on positive response', icon: <SentimentSatisfiedIcon color="success" fontSize="small" />, color: 'success' as const },
    ];

    const hasOpeningScript = (scripts.open ?? '').trim().length > 0;
    const responseScripts = scriptTypes.filter(st => (scripts[st.key] ?? '').trim().length > 0);

    // Nothing to show: no opening script and (no reframing scripts, or reframing hidden for non-coach)
    if (!hasOpeningScript && (responseScripts.length === 0 || (!myRoles?.includes('coach') && !myRoles?.includes('parent')))) {
        return null;
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Box
                sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 1,
                    p: 2,
                }}
            >
                {/* Opening Script */}
                {hasOpeningScript && (
                    <Box data-attr={"opening-question"} sx={{ mb: responseScripts.length > 0 ? 3 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', alignContent: 'center', gap: 1.5 }}>
                            <StartIcon color="primary" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Opening Question</Typography>
                        </Box>
                        <Typography variant="h5" style={{ fontWeight: 600, fontSize: '2rem' }}>{scripts.open ?? ''}</Typography>
                    </Box>
                )}

                {/* Response Scripts (Reframing) - coach-only */}
                {responseScripts.length > 0 && (myRoles?.includes('coach') || myRoles?.includes('parent')) && (
                    <Box data-attr={"reframe-questions"}
                         sx={{ pl: hasOpeningScript ? 2 : 0, borderLeft: hasOpeningScript ? `2px solid ${theme.palette.divider}` : 'none', ml: hasOpeningScript ? 2 : 0 }}>
                        {responseScripts.map((scriptType) => {
                            const scriptContent = scripts[scriptType.key] ?? '';
                            if (!scriptContent.trim()) {
                                return null;
                            }

                            return (
                                <Box key={scriptType.key} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        {scriptType.icon}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                            {scriptType.label}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ pl: 3 }}>
                                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1.2rem', fontStyle: 'italic' }}>{scriptContent}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ScriptsDisplay;
